import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from '../Enums';

export interface IEntitlementProductEntityAttributes extends IModelAttributes {
	name: string;
	productType: Enums.EntitlementProductType | string;
	productDefinition: string; // JSON string payload
	price: number;
	isDisabled: boolean;
	isRestricted: boolean;
	defaultExpiryDurationDays: number;
	order: number;
	termsAndConditions: string;
	priceOverrideCount?: number;
	priceOverrides: Array<
		Models.EntitlementProductPriceOverrideEntity | Models.IEntitlementProductPriceOverrideEntityAttributes
	>;
	userEntitlements: Array<Models.UserEntitlementEntity | Models.IUserEntitlementEntityAttributes>;
}

@entity('EntitlementProductEntity', 'Entitlement Product')
export default class EntitlementProductEntity extends Model implements IEntitlementProductEntityAttributes {
	public static excludeFromCreate: string[] = [];
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<EntitlementProductEntity, string>()
	public name: string;

	@observable
	@attribute<EntitlementProductEntity, Enums.EntitlementProductType | string>()
	public productType: Enums.EntitlementProductType | string;

	@observable
	@attribute<EntitlementProductEntity, string>()
	public productDefinition: string;

	@observable
	@attribute<EntitlementProductEntity, number>()
	public price: number;

	@observable
	@attribute<EntitlementProductEntity, boolean>()
	public isDisabled: boolean;

	@observable
	@attribute<EntitlementProductEntity, boolean>()
	public isRestricted: boolean;

	@observable
	@attribute<EntitlementProductEntity, number>()
	public defaultExpiryDurationDays: number;

	@observable
	@attribute<EntitlementProductEntity, number>()
	public order: number;

	@observable
	@attribute<EntitlementProductEntity, string>()
	public termsAndConditions: string;

	@observable
	@attribute<EntitlementProductEntity, number>()
	public priceOverrideCount?: number;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public priceOverrides: Models.EntitlementProductPriceOverrideEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public userEntitlements: Models.UserEntitlementEntity[] = [];

	constructor(attributes?: Partial<IEntitlementProductEntityAttributes>) {
		super(attributes);
		makeObservable(this);
		this.assignAttributes(attributes);
	}

	public assignAttributes(attributes?: Partial<IEntitlementProductEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.productType !== undefined) {
				this.productType = attributes.productType;
			}
			if (attributes.productDefinition !== undefined) {
				this.productDefinition = attributes.productDefinition;
			}
			if (attributes.price !== undefined) {
				this.price = attributes.price;
			}
			if (attributes.isDisabled !== undefined) {
				this.isDisabled = attributes.isDisabled;
			}
			if (attributes.isRestricted !== undefined) {
				this.isRestricted = attributes.isRestricted;
			}
			if (attributes.defaultExpiryDurationDays !== undefined) {
				this.defaultExpiryDurationDays = attributes.defaultExpiryDurationDays;
			}
			if (attributes.order !== undefined) {
				this.order = attributes.order;
			}
			if (attributes.termsAndConditions !== undefined) {
				this.termsAndConditions = attributes.termsAndConditions;
			}
			if (attributes.priceOverrideCount !== undefined) {
				this.priceOverrideCount = attributes.priceOverrideCount;
			}
			if (attributes.priceOverrides !== undefined && attributes.priceOverrides !== null) {
				this.priceOverrides = attributes.priceOverrides.map(o =>
					o instanceof Models.EntitlementProductPriceOverrideEntity
						? o
						: new Models.EntitlementProductPriceOverrideEntity(o),
				);
			}
		}
	}
}
